import { ActionIcon, Button, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { observer } from "mobx-react";
import { MdBedtime, MdOutlineTimerOff } from "react-icons/md";
import { useStores } from "@/hooks/useStores";

const PRESETS = [10, 20, 30, 60];
const EXTEND_MINUTES = 10;

const countdown = (milliseconds: number) => {
    const total = Math.ceil(milliseconds / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, "0");
    return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const confirm = (message: string) => notifications.show({ message, color: "teal", autoClose: 2500 });

/** Menu to stop playback after a while or at the end of the episode, with the time left when set. */
export const SleepTimerButton = observer(() => {
    const { sleepTimerStore, playerStore } = useStores();
    const remainingMs = sleepTimerStore.remainingMs;
    const episodeEnd = sleepTimerStore.mode?.kind === "episode-end";

    return (
        <Menu position="top-end" shadow="md" width={240} withinPortal zIndex={10003}>
            <Menu.Target>
                {sleepTimerStore.isActive ? (
                    <Button
                        aria-label={
                            episodeEnd
                                ? "Insomningstimer: stannar när avsnittet är slut"
                                : `Insomningstimer: ${Math.ceil((remainingMs ?? 0) / 60_000)} minuter kvar`
                        }
                        leftSection={<MdBedtime size={18} />}
                        radius="xl"
                        size="compact-md"
                        variant="light">
                        {episodeEnd ? "Avsnittet slut" : countdown(remainingMs ?? 0)}
                    </Button>
                ) : (
                    <ActionIcon aria-label="Insomningstimer" radius="xl" size="lg" variant="subtle">
                        <MdBedtime size={18} />
                    </ActionIcon>
                )}
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>Stoppa uppspelningen efter</Menu.Label>
                {PRESETS.map((minutes) => (
                    <Menu.Item
                        key={minutes}
                        onClick={() => {
                            sleepTimerStore.start(minutes);
                            confirm(`Uppspelningen stoppar om ${minutes} minuter`);
                        }}>
                        {minutes} minuter
                    </Menu.Item>
                ))}
                <Menu.Item
                    onClick={() => {
                        sleepTimerStore.startAtEpisodeEnd(String(playerStore.currentEpisode.id));
                        confirm("Uppspelningen stoppar när avsnittet är slut");
                    }}>
                    När avsnittet är slut
                </Menu.Item>
                {sleepTimerStore.isActive && (
                    <>
                        <Menu.Divider />
                        {!episodeEnd && (
                            <Menu.Item
                                onClick={() => {
                                    sleepTimerStore.extend(EXTEND_MINUTES);
                                    confirm(`Förlängde med ${EXTEND_MINUTES} minuter`);
                                }}>
                                Förläng med {EXTEND_MINUTES} minuter
                            </Menu.Item>
                        )}
                        <Menu.Item
                            leftSection={<MdOutlineTimerOff size={16} />}
                            onClick={() => {
                                sleepTimerStore.cancel();
                                confirm("Insomningstimern är avstängd");
                            }}>
                            Avbryt timern
                        </Menu.Item>
                    </>
                )}
            </Menu.Dropdown>
        </Menu>
    );
});
